import { useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { loginInputSchema, PRODUCT_NAME, type LoginInput } from "@repo/shared";
import { useTranslation } from "react-i18next";

import {
  Alert,
  AuthForm,
  AuthPanel,
  AuthPending,
  Button,
  Field,
  Input,
  PasswordInput,
} from "@h2j-data-team/ui";

import { useSsoStatus } from "@/features/sso/api";
import { authClient } from "@/lib/auth-client";
import { sanitizeRedirect, signInWithH2j } from "@/lib/sso";

type LoginSearch = {
  mode: string | undefined;
  token: string | undefined;
  error: string | undefined;
  redirect: string | undefined;
  email: string | undefined;
  /** Emergency-account form after the switch to Login H2J: an admin-only URL, no link on screen. */
  local?: true;
};

export const Route = createFileRoute("/_auth/login")({
  validateSearch: (search: Record<string, unknown>): LoginSearch => {
    return {
      mode: typeof search.mode === "string" ? search.mode : undefined,
      token: typeof search.token === "string" ? search.token : undefined,
      // `error` is the code the Login H2J callback sends back (Better Auth `?error=`).
      error: typeof search.error === "string" ? search.error : undefined,
      redirect: sanitizeRedirect(typeof search.redirect === "string" ? search.redirect : undefined),
      email: typeof search.email === "string" ? search.email : undefined,
      // The router parses `?local=1` into the number 1.
      ...(search.local === true || search.local === 1 || search.local === "1"
        ? { local: true as const }
        : {}),
    };
  },
  head: () => ({ meta: [{ title: `Sign in — ${PRODUCT_NAME}` }] }),
  component: LoginPage,
});

/** Callback error code → text key. No linked account / sign-up off: access is by invitation. */
function ssoErrorKey(code: string): string {
  return code === "account_not_linked" || code === "signup_disabled"
    ? "sso.errors.noAccess"
    : "sso.errors.generic";
}

function LoginPage() {
  const { t: tc } = useTranslation("common");
  const { local } = Route.useSearch();
  const sso = useSsoStatus();

  // Without the answer the password form would flash before the switch hides it.
  if (sso.isLoading) {
    return <AuthPending label={tc("loading")} />;
  }

  return sso.ssoOnly && !local ? (
    <SsoOnlySignIn />
  ) : (
    <LoginForm ssoEnabled={sso.enabled} emergency={sso.ssoOnly} />
  );
}

/** Starts the Login H2J sign-in; `onError` gets the text to show when it can't start. */
function useSsoStart(onError: (message: string | null) => void) {
  const { t } = useTranslation("auth");
  const { redirect } = Route.useSearch();
  const [loading, setLoading] = useState(false);

  const start = async () => {
    onError(null);
    setLoading(true);
    try {
      await signInWithH2j(redirect ?? "/dashboard");
    } catch {
      onError(t("sso.startFailed"));
      setLoading(false);
    }
  };
  return { start, loading };
}

/** After the switch only Login H2J signs people in; the password form is for emergency accounts. */
function SsoOnlySignIn() {
  const { t } = useTranslation("auth");
  const { error: ssoError } = Route.useSearch();
  const [startError, setStartError] = useState<string | null>(null);
  const { start, loading } = useSsoStart(setStartError);
  const error =
    startError ?? (ssoError ? t(ssoErrorKey(ssoError), { productName: PRODUCT_NAME }) : null);

  return (
    <AuthPanel
      title={t("signIn.heading")}
      description={t("sso.onlyDescription")}
      notice={error ? <Alert tone="danger">{error}</Alert> : undefined}
      actions={
        <Button size="lg" onClick={() => void start()} loading={loading}>
          {t("sso.button")}
        </Button>
      }
    />
  );
}

function LoginForm({ ssoEnabled, emergency }: { ssoEnabled: boolean; emergency: boolean }) {
  const navigate = useNavigate();
  const { t } = useTranslation("auth");
  const { redirect, email, error: ssoError } = Route.useSearch();
  const [startError, setStartError] = useState<string | null>(null);
  const sso = useSsoStart(setStartError);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginInputSchema),
    defaultValues: { email: email ?? "" },
  });

  const onSubmit = async (data: LoginInput) => {
    setStartError(null);
    const result = await authClient.signIn.email({
      email: data.email,
      password: data.password,
    });

    if (result.error) {
      if (result.error.code === "EMAIL_NOT_VERIFIED") {
        navigate({ to: "/verify-email", search: { email: data.email } });
        return;
      }
      if (result.error.code === "LOCAL_LOGIN_DISABLED") {
        setError("root", { message: t("sso.localLoginDisabled") });
        return;
      }
      setError("root", { message: result.error.message ?? t("signIn.failed") });
      return;
    }

    const safeRedirect = sanitizeRedirect(redirect);
    if (safeRedirect) {
      window.location.assign(safeRedirect);
    } else {
      navigate({ to: "/dashboard" });
    }
  };

  return (
    <AuthForm
      noValidate
      title={t("signIn.heading")}
      description={t("signIn.description", { productName: PRODUCT_NAME })}
      error={
        errors.root?.message ??
        startError ??
        (ssoError ? t(ssoErrorKey(ssoError), { productName: PRODUCT_NAME }) : undefined)
      }
      busy={isSubmitting}
      submitLabel={t("signIn.submit")}
      submittingLabel={t("signIn.signingIn")}
      onSubmit={handleSubmit(onSubmit)}
      notice={emergency ? <Alert tone="warning">{t("sso.emergencyNotice")}</Alert> : undefined}
      actions={
        ssoEnabled ? (
          <Button
            size="lg"
            variant="outline"
            onClick={() => void sso.start()}
            loading={sso.loading}
            disabled={isSubmitting}
          >
            {t("sso.button")}
          </Button>
        ) : undefined
      }
      footer={<Link to="/forgot-password">{t("signIn.forgotPassword")}</Link>}
    >
      <Field id="email" label={t("emailLabel")} error={errors.email?.message}>
        <Input
          type="email"
          autoComplete="username"
          placeholder={t("emailPlaceholder")}
          {...register("email")}
        />
      </Field>

      <Field id="password" label={t("passwordLabel")} error={errors.password?.message}>
        <PasswordInput
          autoComplete="current-password"
          showLabel={t("showPassword")}
          hideLabel={t("hidePassword")}
          {...register("password")}
        />
      </Field>
    </AuthForm>
  );
}
